import { useQuery } from "@tanstack/react-query";

import { getPerf, perfKey, type FreshnessMeta } from "./api";
import { CampaignLogTab } from "./CampaignLogTab";
import { ChartStylesTab } from "./ChartStylesTab";
import { COMING_NEXT, ComingNext } from "./ComingNext";
import { EmailTab } from "./EmailTab";
import { OverviewTab } from "./OverviewTab";
import { PerformanceTab } from "./PerformanceTab";

/**
 * The freshness bridge (native-module contract step 2). It reads `/perf`, the
 * telemetry the Overview is built from, under the same query key, so the
 * bridge and the Overview are one cache entry. `enabled` rather than a
 * conditional call: `useNativeModuleMeta` calls this on every render.
 */
export function useCvceMarketingMeta(enabled: boolean): FreshnessMeta | null | undefined {
  const head = useQuery({ queryKey: perfKey, queryFn: getPerf, enabled });
  return enabled ? head.data?.meta : undefined;
}

/**
 * CVCE Marketing, rendered natively in the portal. `decision` is not read: the
 * shell has already wrapped this in `PermissionGate` (I-7). An unknown tab
 * falls back to Overview rather than a blank viewport.
 */
export function CvceMarketingModule({
  activeTabKey,
  areaName,
}: {
  decision: "edit" | "view" | "none";
  activeTabKey: string | null;
  areaName: string;
  ownerName?: string | null;
}) {
  if (activeTabKey === "chart_styles") return <ChartStylesTab areaName={areaName} />;
  if (activeTabKey === "campaign_log") return <CampaignLogTab areaName={areaName} />;
  if (activeTabKey === "performance") return <PerformanceTab areaName={areaName} />;
  if (activeTabKey === "email") return <EmailTab areaName={areaName} />;
  if (activeTabKey && activeTabKey in COMING_NEXT) return <ComingNext tabKey={activeTabKey} />;
  return <OverviewTab areaName={areaName} />;
}
